import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { exportToExcel, exportToPDF } from "@/lib/exports";
import { FileSpreadsheet, FileDown, Activity, GitCompare, X, ArrowRight } from "lucide-react";

const ACTION_COLOR = {
  create: "bg-emerald-100 text-emerald-800",
  update: "bg-blue-100 text-blue-800",
  delete: "bg-red-100 text-red-800",
  upsert: "bg-indigo-100 text-indigo-800",
  upload: "bg-purple-100 text-purple-800",
  receive: "bg-amber-100 text-amber-800",
};

const RESOURCES = ["", "vehicle", "transfer", "finance", "user", "finance_doc",
                   "incentive_rule", "profit_ledger", "branch_target", "financer", "quotation"];
const ACTIONS = ["", "create", "update", "delete", "upsert", "upload", "receive", "backfill", "bulk_create", "delete_all"];

const FIELD_LABELS = {
  monthly_target: "Monthly Target",
  ex_showroom_price: "Ex-Showroom Price",
  insurance_price: "Insurance",
  rto_charges: "RTO Charges",
  permit_charges: "Permit Charges",
  earthing_charges: "Earthing Charges",
  file_charges: "File Charges",
  loan_amount: "Loan Amount",
  payment_type: "Payment Type",
  date_of_delivery: "Date of Delivery",
  financer_name: "Financer",
  customer_name: "Customer Name",
  customer_phone: "Customer Phone",
  customer_address: "Address",
  total_vehicle_price: "Total Price",
  total_paid: "Total Paid",
  remaining_balance: "Balance",
  fixed_amount: "Fixed Amount",
  vehicle_model: "Vehicle Model",
  sub_dealer: "Sub-Dealer",
  incentive_amount: "Incentive",
  paid_at: "Paid At",
  chassis_number: "Chassis",
  engine_number: "Engine",
  model_code: "Model Code",
  manufacturing_year: "Year",
  ndp: "NDP (₹)",
};

const humanize = (k) => FIELD_LABELS[k] || k.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

const displayValue = (v) => {
  if (v === null || v === undefined || v === "") return <span className="text-slate-300 italic">empty</span>;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") return v.toLocaleString("en-IN");
  return String(v);
};

const eq = (a, b) => {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (typeof a === "number" && typeof b === "number") return Math.abs(a - b) < 0.001;
  return String(a) === String(b);
};

export default function Audit() {
  const [rows, setRows] = useState([]);
  const [action, setAction] = useState("");
  const [resource, setResource] = useState("");
  const [diffRow, setDiffRow] = useState(null);

  const fetchAll = async () => {
    const params = {};
    if (action) params.action = action;
    if (resource) params.resource = resource;
    const { data } = await api.get("/audit", { params });
    setRows(data);
  };

  useEffect(() => { fetchAll(); /* eslint-disable-line */ }, [action, resource]);

  const columns = [
    { key: "created_at", label: "Time", fmt: (v) => new Date(v).toLocaleString("en-IN") },
    { key: "action", label: "Action" },
    { key: "resource", label: "Resource" },
    { key: "resource_id", label: "Resource ID" },
    { key: "actor_name", label: "Actor" },
    { key: "actor_role", label: "Role" },
    { key: "changes", label: "Changes", fmt: (v) => JSON.stringify(v || {}) },
  ];

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="audit-heading">Audit Log</h1>
          <p className="text-sm text-slate-500 mt-1">Chronological trail of every mutating action — click <span className="font-medium">Diff</span> on any update to see before → after</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="audit-export-excel" onClick={() => exportToExcel("audit_log", rows, columns)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-300 hover:bg-slate-50 text-sm">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
          <button data-testid="audit-export-pdf" onClick={() => exportToPDF("Audit Log", rows, columns)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-300 hover:bg-slate-50 text-sm">
            <FileDown className="w-4 h-4" /> PDF
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 flex gap-3">
        <label className="text-sm">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Action</div>
          <select value={action} onChange={(e) => setAction(e.target.value)} data-testid="audit-filter-action"
            className="px-3 py-2 rounded-md border border-slate-300 text-sm bg-white">
            {ACTIONS.map(a => <option key={a} value={a}>{a || "All"}</option>)}
          </select>
        </label>
        <label className="text-sm">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Resource</div>
          <select value={resource} onChange={(e) => setResource(e.target.value)} data-testid="audit-filter-resource"
            className="px-3 py-2 rounded-md border border-slate-300 text-sm bg-white">
            {RESOURCES.map(r => <option key={r} value={r}>{r || "All"}</option>)}
          </select>
        </label>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg table-scroll">
        <table className="data-table w-full" data-testid="audit-table">
          <thead>
            <tr>
              <th>Time</th><th>Action</th><th>Resource</th><th>Actor</th><th>Changes</th><th className="text-right pr-6">Diff</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-slate-400 text-sm">
                <Activity className="w-8 h-8 mx-auto mb-2 opacity-30" />
                No audit entries match your filters
              </td></tr>
            )}
            {rows.map(r => {
              const hasDiff = !!(r.before && r.after);
              return (
                <tr key={r.id} data-testid={`audit-row-${r.id}`}>
                  <td className="text-xs text-slate-500 whitespace-nowrap">{new Date(r.created_at).toLocaleString("en-IN")}</td>
                  <td>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${ACTION_COLOR[r.action] || "bg-slate-100 text-slate-800"}`}>
                      {r.action}
                    </span>
                  </td>
                  <td>
                    <div className="text-sm">{r.resource}</div>
                    {r.resource_id && <div className="text-[10px] font-mono text-slate-400">{r.resource_id.slice(0, 8)}</div>}
                  </td>
                  <td>
                    <div className="text-sm font-medium text-slate-900">{r.actor_name || "—"}</div>
                    <div className="text-xs text-slate-500 uppercase">{r.actor_role}</div>
                  </td>
                  <td className="text-xs text-slate-600 font-mono max-w-md truncate">{JSON.stringify(r.changes || {})}</td>
                  <td className="text-right pr-6">
                    {hasDiff ? (
                      <button
                        data-testid={`audit-diff-${r.id}`}
                        onClick={() => setDiffRow(r)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs border border-blue-200">
                        <GitCompare className="w-3 h-3" /> Diff
                      </button>
                    ) : (
                      <span className="text-[11px] text-slate-300">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {diffRow && <DiffPanel row={diffRow} onClose={() => setDiffRow(null)} />}
    </div>
  );
}

function DiffPanel({ row, onClose }) {
  const { before = {}, after = {}, actor_name, actor_role, resource, created_at } = row;

  const allFields = useMemo(() => {
    const set = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
    return Array.from(set);
  }, [before, after]);

  const changed = allFields.filter(k => !eq(before[k], after[k]));
  const unchanged = allFields.filter(k => eq(before[k], after[k]));

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="audit-diff-panel">
      <div className="flex-1 bg-slate-900/40" onClick={onClose} />
      <div className="w-full max-w-2xl bg-white shadow-2xl h-full flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Audit Diff</div>
            <h2 className="font-heading text-xl font-semibold text-slate-900 capitalize">
              {resource} update
            </h2>
            <div className="text-xs text-slate-500 mt-1">
              by <span className="font-medium text-slate-700">{actor_name}</span> ({actor_role}) · {new Date(created_at).toLocaleString("en-IN")}
            </div>
          </div>
          <button data-testid="audit-diff-close" onClick={onClose}
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {changed.length === 0 && (
            <div className="text-center py-10 text-slate-400 text-sm">
              No tracked fields changed on this entry.
            </div>
          )}

          {changed.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-2">
                <span>Changed Fields</span>
                <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-semibold">{changed.length}</span>
              </div>
              <div className="border border-amber-200 rounded-lg overflow-hidden mb-6">
                <table className="w-full text-sm">
                  <thead className="bg-amber-50 text-xs uppercase tracking-wider text-amber-800">
                    <tr>
                      <th className="px-3 py-2 text-left">Field</th>
                      <th className="px-3 py-2 text-left">Before</th>
                      <th className="px-3 py-2 w-6 text-center"></th>
                      <th className="px-3 py-2 text-left">After</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-amber-100">
                    {changed.map(k => (
                      <tr key={k} data-testid={`diff-row-${k}`} className="hover:bg-amber-50/50">
                        <td className="px-3 py-2 font-medium text-slate-700 whitespace-nowrap">{humanize(k)}</td>
                        <td className="px-3 py-2 text-slate-500 line-through">{displayValue(before[k])}</td>
                        <td className="px-3 py-2 text-center text-amber-500"><ArrowRight className="w-4 h-4 inline" /></td>
                        <td className="px-3 py-2 text-emerald-700 font-semibold">{displayValue(after[k])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {unchanged.length > 0 && (
            <details className="text-sm">
              <summary className="cursor-pointer text-xs uppercase tracking-wider text-slate-500 hover:text-slate-700 py-2">
                Unchanged fields ({unchanged.length})
              </summary>
              <div className="border border-slate-200 rounded-lg overflow-hidden mt-2">
                <table className="w-full">
                  <tbody className="divide-y divide-slate-100">
                    {unchanged.map(k => (
                      <tr key={k} className="text-slate-500">
                        <td className="px-3 py-1.5 font-medium whitespace-nowrap w-40">{humanize(k)}</td>
                        <td className="px-3 py-1.5">{displayValue(after[k])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}
